import { useState, useEffect } from "react";
import "../assets/ScheduleStopList.css";
import {
  formatDirectionLabel,
  sortDirections,
} from "../utils/directionLabels";

/**
 * Stable column key: same physical stop can appear as multiple CSV columns (e.g. Route 3 Target N vs P).
 */
function columnKeyForStop(stop) {
  const rawTitle = stop.bus_stop_title || "";
  const columnLabel = stop.column_label || rawTitle;
  const tripColumn = Number.isInteger(stop.trip_column) ? stop.trip_column : null;
  const isLoopStop =
    stop.is_loop === true || stop.is_loop === 1 || stop.is_loop === "1";
  const baseId = stop.bus_stop_id || "--";
  if (tripColumn !== null) {
    return `${baseId}::c${tripColumn}`;
  }
  if (isLoopStop) {
    return `${baseId}-loop-${columnLabel}`;
  }
  return `${baseId}::${columnLabel}`;
}

/** Minutes past midnight before which times belong to overnight / prior service day. */
const OVERNIGHT_CUTOFF_MINUTES = 4 * 60;

/**
 * Sort key for a stop time on a transit service day.
 * Times before 4:00 AM are treated as after midnight (prior evening + 24h).
 */
function tripSortMinutes(stop) {
  const t = stop?.time;
  if (!t || !/^\d{1,2}:\d{2}$/.test(t)) {
    return null;
  }
  const [h, m] = t.split(":").map(Number);
  let mins = h * 60 + m;
  if (mins < OVERNIGHT_CUTOFF_MINUTES) {
    mins += 24 * 60;
  }
  return mins;
}

/**
 * One table row per CSV trip; cells align across stops (Mon–Sat vs Sunday patterns).
 */
function buildTripAlignedStopColumns(stops, formatTime) {
  const columnMeta = new Map();
  for (const stop of stops) {
    const colKey = columnKeyForStop(stop);
    if (!columnMeta.has(colKey)) {
      columnMeta.set(colKey, {
        tripColumn: Number.isInteger(stop.trip_column) ? stop.trip_column : null,
        sortOrder: stop.sort_order ?? 999999,
        title: stop.column_label || stop.bus_stop_title || `Stop #${stop.bus_stop_id}`,
      });
    } else {
      const meta = columnMeta.get(colKey);
      meta.sortOrder = Math.min(meta.sortOrder, stop.sort_order ?? 999999);
      if (meta.tripColumn === null && Number.isInteger(stop.trip_column)) {
        meta.tripColumn = stop.trip_column;
      }
    }
  }

  const columnKeys = [...columnMeta.keys()].sort(
    (a, b) => {
      const metaA = columnMeta.get(a);
      const metaB = columnMeta.get(b);
      if (metaA.tripColumn !== null && metaB.tripColumn !== null) {
        return metaA.tripColumn - metaB.tripColumn;
      }
      return metaA.sortOrder - metaB.sortOrder;
    }
  );

  const tripsMap = new Map();
  for (const stop of stops) {
    const tr = stop.trip_row;
    const colKey = columnKeyForStop(stop);
    if (!tripsMap.has(tr)) {
      tripsMap.set(tr, new Map());
    }
    tripsMap.get(tr).set(colKey, stop);
  }

  const tripKeys = [...tripsMap.keys()];
  const tripOrderingValue = (tr) => {
    const cells = tripsMap.get(tr);
    let best = null;
    for (const stop of cells.values()) {
      const mins = tripSortMinutes(stop);
      if (mins !== null && (best === null || mins < best)) {
        best = mins;
      }
    }
    return best ?? 999999;
  };
  tripKeys.sort((a, b) => {
    const diff = tripOrderingValue(a) - tripOrderingValue(b);
    if (diff !== 0) {
      return diff;
    }
    return String(a).localeCompare(String(b));
  });

  const stopColumns = columnKeys.map((key) => ({
    id: key,
    title: columnMeta.get(key).title,
    times: [],
  }));
  const colIndex = Object.fromEntries(columnKeys.map((k, i) => [k, i]));

  for (const tr of tripKeys) {
    const cells = tripsMap.get(tr);
    for (const key of columnKeys) {
      const stop = cells.get(key);
      stopColumns[colIndex[key]].times.push(formatTime(stop?.time));
    }
  }

  return stopColumns;
}

/**
 * ScheduleStopList Component
 * 
 * Note: This component fetches all schedule stops data without API-level filtering
 * because the API endpoint is cached. All filtering (direction, day of week) is
 * handled on the frontend to ensure proper functionality when the cached API
 * response doesn't respect filter parameters.
 */
export default function ScheduleStopList({ routeId, direction: initialDirection, dayOfWeek: initialDayOfWeek, onDirectionChange, onDayOfWeekChange, directionLabels = {} }) {
  const [scheduleStops, setScheduleStops] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDirection, setSelectedDirection] = useState(initialDirection || '');
  const [selectedDayOfWeek, setSelectedDayOfWeek] = useState(initialDayOfWeek || '');
  const [availableDirections, setAvailableDirections] = useState([]);
  const [availableDays, setAvailableDays] = useState([]);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);

  // Update selectedDirection when initialDirection prop changes
  useEffect(() => {
    setSelectedDirection(initialDirection || '');
  }, [initialDirection]);

  // Update selectedDayOfWeek when initialDayOfWeek prop changes
  useEffect(() => {
    setSelectedDayOfWeek(initialDayOfWeek || '');
  }, [initialDayOfWeek]);

  useEffect(() => {
    if (!routeId) {
      setScheduleStops(null);
      return;
    }

    const fetchScheduleStops = async () => {
      setLoading(true);
      setError(null);

      try {
        // Always fetch all data without filters since the API is cached
        // We'll handle filtering on the frontend
        const url = `/api/schedule-stops/${routeId}?_format=json`;
        
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        setScheduleStops(data);
        
        // Extract available directions from the data
        const directions = sortDirections(
          [...new Set(data.map(stop => stop.direction).filter(Boolean))]
        );
        setAvailableDirections(directions);

        // Extract available days from the data
        const allDays = data.flatMap(stop => stop.days || []).filter(Boolean);
        const uniqueDays = [...new Set(allDays)];
        
        // Sort days in a consistent order: Weekdays, Friday, Saturday, Sunday
        const dayOrder = ['weekdays', 'friday', 'saturday', 'sunday'];
        const sortedDays = uniqueDays.sort((a, b) => {
          const indexA = dayOrder.indexOf(a.toLowerCase());
          const indexB = dayOrder.indexOf(b.toLowerCase());
          // If both are in the order array, sort by their position
          if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB;
          }
          // If only one is in the order array, prioritize it
          if (indexA !== -1) return -1;
          if (indexB !== -1) return 1;
          // If neither is in the order array, maintain alphabetical order
          return a.localeCompare(b);
        });
        
        setAvailableDays(sortedDays);

        // Determine a sensible default day of week based on today's date
        // Prefer Saturday/Sunday when viewing on those days, otherwise Weekdays
        if (!selectedDayOfWeek && sortedDays.length > 0) {
          const today = new Date().getDay(); // 0 = Sunday, 6 = Saturday

          const findMatchingDay = (label) =>
            sortedDays.find((d) => d.toLowerCase() === label.toLowerCase());

          let defaultDay = '';

          if (today === 6) {
            defaultDay = findMatchingDay('Saturday');
          } else if (today === 0) {
            defaultDay = findMatchingDay('Sunday');
          }

          if (!defaultDay) {
            defaultDay =
              findMatchingDay('Weekdays') ||
              findMatchingDay('Friday') ||
              sortedDays[0];
          }

          if (defaultDay) {
            setSelectedDayOfWeek(defaultDay);
            if (onDayOfWeekChange) {
              onDayOfWeekChange(defaultDay);
            }
          }
        }
      } catch (err) {
        console.error('ScheduleStopList - Error fetching schedule stops:', err);
        setError('Failed to load schedule stops');
      } finally {
        setLoading(false);
      }
    };

    fetchScheduleStops();
  }, [routeId, directionLabels]); // Re-sort labels if route remapping settings change

  // Group schedule stops by bus stop ID
  const groupStopsByBusStop = (stops) => {
    const grouped = {};
    stops.forEach(stop => {
      const rawTitle = stop.bus_stop_title || '';
      const isLoopStop = stop.is_loop === true || stop.is_loop === 1 || stop.is_loop === '1';
      const baseId = stop.bus_stop_id || '--';
      const stopId = columnKeyForStop(stop);
      if (isLoopStop) {
        stop.sort_order = 999999;
      }

      if (!grouped[stopId]) {
        grouped[stopId] = {
          stops: [],
          title: stop.column_label || rawTitle || `Stop #${baseId}`,
          sortOrder: stop.sort_order
        };
      }
      grouped[stopId].stops.push(stop);
    });
    return grouped;
  };

  // Categorize times into periods
  // Format time for display in 12-hour format with AM/PM
  const formatTime = (time) => {
    if (!time) return '--';
    
    // If time is already in HH:MM format, convert to 12-hour
    if (/^\d{1,2}:\d{2}$/.test(time)) {
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${displayHour}:${minutes} ${ampm}`;
    }
    
    // If it's a timestamp, convert to 12-hour format
    const date = new Date(time);
    if (!isNaN(date.getTime())) {
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    }
    
    return time;
  };

  // Sort times within a column by service-day order (overnight after evening; empty last).
  const sortTimes = (times) => {
    return times.sort((a, b) => {
      const minsA = tripSortMinutes(a);
      const minsB = tripSortMinutes(b);
      if (minsA === null && minsB === null) return 0;
      if (minsA === null) return 1;
      if (minsB === null) return -1;
      return minsA - minsB;
    });
  };

  if (loading) {
    return (
      <div className="schedule-stop-list">
        <div className="schedule-stop-list__header" id="schedule-stop-list-header">
          <h4>Schedules</h4>
        </div>
        <div className="schedule-stop-list__loading">
          <span>Loading schedule stops...</span>
          <div className="spinner-border spinner-border-sm ms-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="schedule-stop-list">
        <div className="schedule-stop-list__header" id="schedule-stop-list-header">
          <h4>Schedules</h4>
          <a href="#route-transfers" className="schedule-stop-list__header__cta btn btn-secondary">Jump to Transfers</a>
        </div>
        <div className="schedule-stop-list__error">
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (!scheduleStops || scheduleStops.length === 0) {
    return (
      <div className="schedule-stop-list">
        <div className="schedule-stop-list__header" id="schedule-stop-list-header">
          <h4>Schedules</h4>
          <a href="#route-transfers" className="schedule-stop-list__header__cta btn btn-secondary">Jump to Transfers</a>
        </div>
        <div className="schedule-stop-list__empty">
          <span>No schedule stops available for this route.</span>
        </div>
      </div>
    );
  }

  // Filter stops by selected direction and day of week if selected
  const filteredStops = scheduleStops.filter(stop => {
    // Direction filtering
    const directionMatch = !selectedDirection || 
      (stop.direction && stop.direction.toLowerCase() === selectedDirection.toLowerCase());
    
    // Day of week filtering
    let dayMatch = true;
    if (selectedDayOfWeek) {
      if (!stop.days || !Array.isArray(stop.days) || stop.days.length === 0) {
        dayMatch = false;
      } else if (selectedDayOfWeek === 'Weekdays') {
        // For Weekdays, include stops that have "weekdays" but exclude those that also have "friday"
        // This ensures Friday-only stops don't appear in the Weekdays filter
        dayMatch = stop.days.includes('weekdays') && !stop.days.includes('friday');
      } else {
        // For other days (Friday, Saturday, Sunday), check if the stop has that specific day
        // Handle both capitalized and lowercase versions
        const dayLower = selectedDayOfWeek.toLowerCase();
        dayMatch = stop.days.some(day => day.toLowerCase() === dayLower);
      }
    }
    
    return directionMatch && dayMatch;
  });
    
  const useTripRows =
    filteredStops.length > 0 &&
    filteredStops.every((s) => s.trip_row != null && String(s.trip_row).length > 0);

  let stopColumns;
  if (useTripRows) {
    stopColumns = buildTripAlignedStopColumns(filteredStops, formatTime);
  } else {
    const groupedByStop = groupStopsByBusStop(filteredStops);
    const stopIds = Object.keys(groupedByStop).sort((a, b) => {
      const sortOrderA = groupedByStop[a].sortOrder;
      const sortOrderB = groupedByStop[b].sortOrder;
      return sortOrderA - sortOrderB;
    });
    stopColumns = stopIds.map((stopId) => {
      const stopData = groupedByStop[stopId];
      const sortedTimes = sortTimes([...stopData.stops]);
      return {
        id: stopId,
        title: stopData.title,
        times: sortedTimes.map((stop) => formatTime(stop.time)),
      };
    });
  }

  // Hide columns that are blank for the currently selected day/direction.
  stopColumns = stopColumns.filter((column) =>
    column.times.some((time) => time && time !== "--")
  );

  const maxRows = stopColumns.reduce(
    (max, column) => Math.max(max, column.times.length),
    0
  );

  return (
    <div className="schedule-stop-list">
      <div className="schedule-stop-list__header" id="schedule-stop-list-header">
        <h4>Schedules</h4>
        <a href="#route-transfers" className="schedule-stop-list__header__cta btn btn-secondary">Jump to Transfers</a>
      </div>
      
      {/* Filters Section */}
      {(availableDirections.length > 0 || availableDays.length > 0) && (
        <div className="schedule-stop-list__filters">
          {/* Travel Direction Filters */}
          {availableDirections.length > 0 && (
            <div className="filter-group">
              <div className="schedule-stop-list__filters__header">
                <h6>Travel Direction</h6>
              </div>
              <div className="direction-selector d-flex gap-2">
                {availableDirections.map((direction) => (
                  <button
                    key={direction}
                    type="button"
                    className={`btn d-flex gap-2 ${
                      selectedDirection === direction
                        ? "btn-primary__small"
                        : "btn-primary__reversed__small flex-row-reverse"
                    }`}
                    onClick={() => {
                      // Prevent an "empty" selection for direction
                      // Clicking the already selected direction does nothing; clicking another switches
                      if (selectedDirection === direction) {
                        return;
                      }
                      setSelectedDirection(direction);
                      if (onDirectionChange) {
                        onDirectionChange(direction);
                      }
                    }}
                  >
                    <span className={`icon-arrow-left icon-arrow-${direction.toLowerCase()}`}></span>
                    <span className="eyebrow">
                      {formatDirectionLabel(direction, directionLabels)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Travel Days Filters */}
          {availableDays.length > 0 && (
            <div className="filter-group">
              <div className="schedule-stop-list__filters__header">
                <h6>Travel Days</h6>
              </div>
              <div className="days-selector d-flex gap-2">
                {availableDays.map((day) => (
                  <button
                    key={day}
                    type="button"
                    className={`btn d-flex gap-2 ${
                      selectedDayOfWeek === day
                        ? "btn-primary__small"
                        : "btn-primary__reversed__small"
                    }`}
                    onClick={() => {
                      // Prevent an "empty" selection by keeping the current day selected
                      // Clicking an already selected day does nothing; clicking another day switches to it
                      if (selectedDayOfWeek === day) {
                        return;
                      }
                      setSelectedDayOfWeek(day);
                      if (onDayOfWeekChange) {
                        onDayOfWeekChange(day);
                      }
                    }}
                  >
                    <span className="eyebrow">
                      {day === 'Weekdays' ? 'Weekdays' : 
                       day === 'Friday' ? 'Friday' : 
                       day === 'Saturday' ? 'Saturday' : 
                       day === 'Sunday' ? 'Sunday' : 
                       day.charAt(0).toUpperCase() + day.slice(1)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      <div className="schedule-stop-list__content">
        {stopColumns.length === 0 ? (
          <div className="schedule-stop-list__empty">
            <span>No schedule stops match the selected filters.</span>
          </div>
        ) : (
          <div className="schedule-table-wrapper">
            <table className="table schedule-table mb-0">
              <thead>
                <tr>
                  {stopColumns.map((column) => (
                    <th key={column.id} scope="col">
                      {column.title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: maxRows }).map((_, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className={`schedule-table-row${selectedRowIndex === rowIndex ? ' is-selected' : ''}`}
                    onClick={() =>
                      setSelectedRowIndex(
                        selectedRowIndex === rowIndex ? null : rowIndex
                      )
                    }
                  >
                    {stopColumns.map((column) => (
                      <td key={`${column.id}-${rowIndex}`}>
                        {column.times[rowIndex] || ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}